import { Link } from 'react-router-dom';
import { Plus, Minus, Tag } from '@phosphor-icons/react';
import { useCart } from '@/context/CartContext';
import { formatINR } from '@/lib/api';
import { PRODUCT } from '@/constants/testIds';

export default function ProductCard({ product }) {
  const { qtyOf, add, increment, decrement } = useCart();
  const qty = qtyOf(product.id);
  const hasDiscount = product.mrp && product.mrp > product.price;
  const discount = hasDiscount ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;

  return (
    <div
      data-testid={PRODUCT.card(product.id)}
      className="group bg-white rounded-3xl border border-stone-100/80 shadow-card hover:shadow-card-hover hover:border-brand-200 hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
    >
      <Link to={`/product/${product.id}`} className="relative block aspect-square bg-stone-50/80">
        <img
          src={product.image_url || '/placeholder.png'}
          alt={product.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
        />
        {hasDiscount && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-accent-gradient text-white text-[10px] font-extrabold px-2.5 py-1 rounded-full shadow-cta tracking-wide">
            <Tag size={10} weight="fill" /> {discount}% OFF
          </span>
        )}
      </Link>
      <div className="p-3.5 sm:p-4 flex flex-col gap-2 flex-1">
        <Link to={`/product/${product.id}`}>
          <h3 data-testid={PRODUCT.title(product.id)} className="text-sm sm:text-[15px] font-semibold text-stone-900 line-clamp-2 leading-snug min-h-[2.5rem]">
            {product.name}
          </h3>
        </Link>
        <p className="text-xs text-stone-500 font-medium">{product.unit || '1 unit'}</p>

        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div className="flex flex-col leading-tight">
            <span className="text-xl sm:text-[22px] font-extrabold text-stone-900 tracking-tight">{formatINR(product.price)}</span>
            {hasDiscount && <span className="text-xs text-stone-400 line-through font-medium">{formatINR(product.mrp)}</span>}
          </div>
          {qty === 0 ? (
            <button
              onClick={() => add(product, 1)}
              data-testid={PRODUCT.addBtn(product.id)}
              className="add-pop px-5 h-10 rounded-full bg-accent-gradient text-white text-sm font-extrabold tracking-wider shadow-cta hover:brightness-110 hover:scale-[1.03] active:scale-95 transition-all"
            >
              ADD
            </button>
          ) : (
            <div className="flex items-center gap-1 bg-accent-gradient text-white rounded-full shadow-cta">
              <button onClick={() => decrement(product.id)} data-testid={PRODUCT.decBtn(product.id)} className="w-10 h-10 grid place-items-center hover:brightness-110 rounded-l-full transition-all active:scale-95">
                <Minus size={14} weight="bold" />
              </button>
              <span data-testid={PRODUCT.qty(product.id)} className="text-sm font-extrabold w-6 text-center">{qty}</span>
              <button onClick={() => increment(product.id)} data-testid={PRODUCT.incBtn(product.id)} className="w-10 h-10 grid place-items-center hover:brightness-110 rounded-r-full transition-all active:scale-95">
                <Plus size={14} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
